import { useFlags } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { match } from 'ts-pattern'

import Header from '@/components-latest/layout/Header'
import Spinner from '@/components-latest/primitives/Spinner'
import BillingCycleDetails from '@/components-latest/ui/BillingPage/BillingCycleDetails'
import BillingCyclesList from '@/components-latest/ui/BillingPage/BillingCyclesList'
import EmptyBillingPage from '@/components-latest/ui/BillingPage/EmptyBillingPage'
import { useLazyFetchBillingCyclesQuery } from '@/ducks/billing/api/billing.api'
import {
  IBillingCycle,
  IGetBillingCyclesResponse
} from '@/ducks/billing/billing.types'
import {
  selectPhoneCode,
  selectStoreCashCoStatus,
  selectStoreId
} from '@/ducks/store/selectors/store.selectors'

import Styles from './BillingPage.module.scss'

const BillingPage = () => {
  const { t } = useTranslation()

  const storeId = useSelector(selectStoreId)
  const isStoreUsingCashCo = useSelector(selectStoreCashCoStatus)

  const { lmd_store_webapp_FR_Billing } = useFlags()
  const phoneCode = useSelector(selectPhoneCode)

  const [getBillingCycles, { isFetching, data }] =
    useLazyFetchBillingCyclesQuery()

  useEffect(() => {
    if (!storeId || !isStoreUsingCashCo) return
    getBillingCycles({ storeId })
  }, [storeId, getBillingCycles, isStoreUsingCashCo])

  const [activeCycle, setActiveCycle] = useState<IBillingCycle | null>(null)
  const [activeCycleType, setActiveCycleType] = useState<
    'paid' | 'unpaid' | null
  >(null)

  const openCycleDetailsCreator = (
    cycle: IBillingCycle | null,
    type: 'paid' | 'unpaid'
  ) => {
    if (!cycle || !type) {
      setActiveCycle(null)
      setActiveCycleType(null)
      return
    }
    setActiveCycle(cycle)
    setActiveCycleType(type)
  }

  if (!isStoreUsingCashCo) return null

  if (!lmd_store_webapp_FR_Billing && phoneCode === '+33') return null

  return (
    <div className={Styles.page} data-testid="billing-page">
      <Header
        onGoBack={
          activeCycle
            ? () => {
                setActiveCycle(null)
              }
            : undefined
        }
        title={
          activeCycle
            ? t('billing.page-title-details')
            : t('billing.page-title')
        }
      />
      <div className={Styles.content}>
        {match({ isFetching, data, activeCycle })
          .when(
            ({ data, isFetching }) => !data || isFetching,
            () => (
              <div className={Styles.spinner}>
                <Spinner />
              </div>
            )
          )
          .when(
            ({ data }) =>
              data?.paid_billing?.length === 0 &&
              data?.unpaid_billing?.length === 0,
            () => <EmptyBillingPage />
          )
          .when(
            ({ activeCycle }) => !activeCycle,
            () => {
              const { paid_billing, unpaid_billing } =
                data as IGetBillingCyclesResponse
              return (
                <>
                  <BillingCyclesList
                    onDetailsClick={openCycleDetailsCreator}
                    cycles={unpaid_billing}
                    type="unpaid"
                  />
                  <BillingCyclesList
                    onDetailsClick={openCycleDetailsCreator}
                    cycles={paid_billing}
                    type="paid"
                  />
                </>
              )
            }
          )
          .when(
            ({ activeCycle }) => activeCycle,
            () => {
              return (
                <BillingCycleDetails
                  cycle={activeCycle}
                  type={activeCycleType}
                />
              )
            }
          )
          .otherwise(() => null)}
      </div>
    </div>
  )
}

export default BillingPage
